export interface Vector2 {
  x: number;
  y: number;
}

export interface VisionCone {
  origin: Vector2;
  direction: Vector2;
  range: number;
  halfAngleRad: number;
}

export interface CoverAabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const INSIDE = 0;
const LEFT = 1;
const RIGHT = 2;
const BOTTOM = 4;
const TOP = 8;

export class PerceptionSystem {
  canSee(cone: VisionCone, target: Vector2, covers: readonly CoverAabb[] = []): boolean {
    const dx = target.x - cone.origin.x;
    const dy = target.y - cone.origin.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > cone.range * cone.range) return false;

    const dist = Math.sqrt(distSq);
    if (dist < 1e-6) return true;

    const invDist = 1 / dist;
    const tx = dx * invDist;
    const ty = dy * invDist;

    const dlen = Math.hypot(cone.direction.x, cone.direction.y);
    if (dlen < 1e-6) return false;
    const cdx = cone.direction.x / dlen;
    const cdy = cone.direction.y / dlen;

    const cosAngle = tx * cdx + ty * cdy;
    const cosHalf = Math.cos(cone.halfAngleRad);
    if (cosAngle < cosHalf) return false;

    for (const cover of covers) {
      if (this.targetInside(target, cover)) continue;
      if (this.segmentIntersectsAabb(cone.origin, target, cover)) return false;
    }
    return true;
  }

  canHear(listener: Vector2, sound: Vector2, radius: number): boolean {
    const dx = sound.x - listener.x;
    const dy = sound.y - listener.y;
    return dx * dx + dy * dy <= radius * radius;
  }

  isInsideCover(point: Vector2, covers: readonly CoverAabb[]): boolean {
    for (const c of covers) {
      if (this.targetInside(point, c)) return true;
    }
    return false;
  }

  private targetInside(p: Vector2, b: CoverAabb): boolean {
    return p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY;
  }

  private regionCode(p: Vector2, b: CoverAabb): number {
    let code = INSIDE;
    if (p.x < b.minX) code |= LEFT;
    else if (p.x > b.maxX) code |= RIGHT;
    if (p.y < b.minY) code |= BOTTOM;
    else if (p.y > b.maxY) code |= TOP;
    return code;
  }

  private segmentIntersectsAabb(a: Vector2, b: Vector2, box: CoverAabb): boolean {
    let ax = a.x;
    let ay = a.y;
    let bx = b.x;
    let by = b.y;
    let ca = this.regionCode({ x: ax, y: ay }, box);
    let cb = this.regionCode({ x: bx, y: by }, box);

    for (let i = 0; i < 8; i++) {
      if ((ca | cb) === 0) return true;
      if ((ca & cb) !== 0) return false;

      const outside = ca !== 0 ? ca : cb;
      let x = 0;
      let y = 0;

      if (outside & TOP) {
        const t = (box.maxY - ay) / (by - ay);
        x = ax + (bx - ax) * t;
        y = box.maxY;
      } else if (outside & BOTTOM) {
        const t = (box.minY - ay) / (by - ay);
        x = ax + (bx - ax) * t;
        y = box.minY;
      } else if (outside & RIGHT) {
        const t = (box.maxX - ax) / (bx - ax);
        y = ay + (by - ay) * t;
        x = box.maxX;
      } else if (outside & LEFT) {
        const t = (box.minX - ax) / (bx - ax);
        y = ay + (by - ay) * t;
        x = box.minX;
      }

      if (outside === ca) {
        ax = x;
        ay = y;
        ca = this.regionCode({ x: ax, y: ay }, box);
      } else {
        bx = x;
        by = y;
        cb = this.regionCode({ x: bx, y: by }, box);
      }
    }
    return false;
  }
}
